import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    ListTodo, Activity, Timer, Calendar, Briefcase, GitBranch, Star,
    CircleAlert, CircleCheck, Video, ChevronRight, Users,
} from "lucide-react";

const DONE_KEY = "myweek_done_v1";

function weekStamp() {
    const d = new Date();
    const onejan = new Date(d.getFullYear(), 0, 1);
    return `${d.getFullYear()}w${Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7)}`;
}

function readDone() {
    try { return JSON.parse(localStorage.getItem(DONE_KEY) || "{}"); } catch { return {}; }
}

const SEV_ORDER = { high: 0, medium: 1, ok: 2 };

function healthTone(score) {
    if (score >= 70) return "border-emerald-300 bg-emerald-50 text-emerald-700";
    if (score >= 40) return "border-amber-300 bg-amber-50 text-amber-700";
    return "border-rose-300 bg-rose-50 text-rose-700";
}

function urgencyRank(j) {
    const sla = j.sla_status === "breach" ? 0 : j.sla_status === "warning" ? 1 : 2;
    return sla * 1000 + (j.health?.score ?? 100);
}

export default function MyWeek() {
    const { user } = useAuth();
    const isPrivileged = ["admin", "employer"].includes(user.role);
    const [viewerId, setViewerId] = useState(user.id);
    const [recruiters, setRecruiters] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [interviews, setInterviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [done, setDone] = useState(readDone);
    const stamp = weekStamp();
    const isViewingSelf = viewerId === user.id;

    // Load recruiter list (once) so admins/employers can pick anyone
    useEffect(() => {
        if (!isPrivileged) return;
        (async () => {
            try {
                const { data } = await api.get("/company/recruiters");
                const list = (data?.items || data || []);
                // Include self at the top
                setRecruiters([{ id: user.id, name: `${user.name || user.email} (me)`, email: user.email }, ...list.filter((r) => r.id !== user.id)]);
            } catch (err) { /* silent — falls back to self only */ }
        })();
    }, [isPrivileged, user.id, user.name, user.email]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const from = new Date().toISOString().slice(0, 10);
                const to = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
                const [smart, hist] = await Promise.all([
                    api.get("/pipeline/smart", { params: { include_apps: 0 } }),
                    api.get("/interviews/history", { params: { period_from: from, period_to: to + "T23:59:59.999Z" } }),
                ]);
                const mine = (smart.data.jobs || [])
                    .filter((j) => (j.assignments || []).some((a) => a.user_id === viewerId))
                    .sort((a, b) => urgencyRank(a) - urgencyRank(b) || SEV_ORDER[a.nba?.severity] - SEV_ORDER[b.nba?.severity]);
                setJobs(mine);
                setInterviews((hist.data.items || [])
                    .filter((r) => r.created_by === viewerId || (r.interviewer_ids || []).includes(viewerId))
                    .filter((r) => (r.status || "Scheduled").toLowerCase() !== "cancelled")
                    .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")));
            } catch (err) { toast.error(formatApiError(err)); }
            finally { setLoading(false); }
        })();
    }, [viewerId]);

    const taskKey = (j) => `${stamp}:${viewerId}:${j.id}:${j.nba?.code}`;
    const toggleDone = (j) => {
        if (!isViewingSelf) return;   // read-only for admin viewing others
        setDone((cur) => {
            const next = { ...cur, [taskKey(j)]: !cur[taskKey(j)] };
            localStorage.setItem(DONE_KEY, JSON.stringify(next));
            return next;
        });
    };

    const stats = useMemo(() => ({
        jobs: jobs.length,
        breaches: jobs.filter((j) => j.sla_status === "breach").length,
        doneCount: jobs.filter((j) => done[taskKey(j)]).length,
        interviews: interviews.length,
    }), [jobs, interviews, done]); // eslint-disable-line react-hooks/exhaustive-deps

    const myAssignment = (j) => (j.assignments || []).find((a) => a.user_id === viewerId);

    const viewerLabel = isViewingSelf ? "your" : (recruiters.find((r) => r.id === viewerId)?.name || "recruiter").replace(" (me)", "");

    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="my-week-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Daily driver</div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">{isViewingSelf ? "My week" : `${viewerLabel}'s week`}</h1>
                    <p className="text-foreground/80 mt-2 text-sm">{isViewingSelf ? "Your" : `${viewerLabel}'s`} assigned jobs sorted by urgency, with the single next best action for each — work the list top to bottom.</p>
                </div>
                {isPrivileged && recruiters.length > 1 && (
                    <label className="inline-flex items-center gap-2 border border-border bg-white px-2 py-1.5" data-testid="myweek-viewer-picker-wrap">
                        <Users className="w-3.5 h-3.5 text-ikb" />
                        <select
                            value={viewerId}
                            onChange={(e) => setViewerId(e.target.value)}
                            className="h-8 pl-1 pr-6 text-sm bg-white border-0 focus:outline-none"
                            data-testid="myweek-viewer-picker"
                        >
                            {recruiters.map((r) => (
                                <option key={r.id} value={r.id}>{r.name || r.email}</option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 border border-border bg-white divide-x divide-border" data-testid="my-week-stats">
                {[["My jobs", stats.jobs, ""], ["SLA breaches", stats.breaches, stats.breaches ? "text-rose-700" : ""],
                  ["Actions done", `${stats.doneCount}/${stats.jobs}`, stats.doneCount === stats.jobs && stats.jobs ? "text-emerald-700" : ""],
                  ["Interviews · 7d", stats.interviews, ""]].map(([label, val, cls]) => (
                    <div key={label} className="p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{label}</div>
                        <div className={`font-display font-black text-2xl mt-0.5 ${cls}`}>{val}</div>
                    </div>
                ))}
            </div>

            {loading ? (
                <div className="text-sm font-mono text-foreground/70">Loading…</div>
            ) : jobs.length === 0 ? (
                <div className="border border-dashed border-border bg-white p-12 text-center">
                    <ListTodo className="w-8 h-8 mx-auto text-foreground/30" />
                    <h3 className="font-display font-bold text-lg mt-3">No jobs assigned to you yet</h3>
                    <p className="text-sm text-foreground/70 mt-1">Ask your team lead to assign you on the <Link to="/pipeline" className="text-ikb underline">pipeline</Link> or <Link to="/jobs" className="text-ikb underline">jobs</Link> page.</p>
                </div>
            ) : (
                <div className="space-y-2" data-testid="my-week-tasks">
                    {jobs.map((j) => {
                        const isDone = !!done[taskKey(j)];
                        const nba = j.nba || {};
                        const mine = myAssignment(j);
                        const Icon = nba.severity === "ok" ? CircleCheck : CircleAlert;
                        const iconCls = nba.severity === "high" ? "text-rose-600" : nba.severity === "medium" ? "text-amber-600" : "text-emerald-600";
                        return (
                            <div key={j.id} className={`border bg-white p-3.5 transition-opacity ${isDone ? "opacity-50" : ""} ${nba.severity === "high" && !isDone ? "border-rose-200" : "border-border"}`} data-testid={`my-week-task-${j.id}`}>
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={isDone}
                                        onChange={() => toggleDone(j)}
                                        className="mt-1 w-4 h-4 accent-[#002FA7] cursor-pointer shrink-0"
                                        data-testid={`my-week-done-${j.id}`}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className={`text-sm flex items-start gap-1.5 ${isDone ? "line-through" : ""}`}>
                                            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconCls}`} />
                                            <span>{nba.text || "Review this job."}</span>
                                        </div>
                                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                                            <Link to={`/jobs/${j.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-ikb hover:underline">
                                                <Briefcase className="w-3 h-3" /> {j.title}
                                            </Link>
                                            {mine?.role === "lead" && <Star className="w-3 h-3 fill-amber-500 text-amber-500" title="You are the lead" />}
                                            <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 inline-flex items-center gap-1 ${healthTone(j.health?.score ?? 0)}`}>
                                                <Activity className="w-3 h-3" /> {j.health?.score ?? 0}
                                            </span>
                                            <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5 inline-flex items-center gap-1 text-foreground/70">
                                                <Timer className="w-3 h-3" /> {j.days_open}d open
                                            </span>
                                            {j.sla_status && (
                                                <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${j.sla_status === "breach" ? "border-rose-300 bg-rose-50 text-rose-700 font-bold" : j.sla_status === "warning" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                                                    SLA {j.sla_status === "breach" ? `+${j.days_open - j.sla_days}d over` : `${Math.max(0, j.sla_days - j.days_open)}d left`}
                                                </span>
                                            )}
                                            {mine && Object.keys(mine.targets || {}).length > 0 && (
                                                <span className="text-[10px] font-mono text-foreground/60">
                                                    {Object.entries(mine.targets).map(([k, t]) => `${k} ${mine.actuals?.[k] || 0}/${t}`).join(" · ")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <Link to="/pipeline" className="shrink-0 p-1.5 border border-border hover:border-ikb hover:text-ikb transition-colors" title="Open pipeline" data-testid={`my-week-pipeline-${j.id}`}>
                                        <GitBranch className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <section>
                <h2 className="font-display font-bold text-lg flex items-center gap-2"><Calendar className="w-4 h-4 text-ikb" /> My interviews · next 7 days</h2>
                {interviews.length === 0 ? (
                    <p className="text-sm text-foreground/60 mt-2">Nothing scheduled — go book some from the pipeline.</p>
                ) : (
                    <div className="mt-3 border border-border bg-white divide-y divide-border" data-testid="my-week-interviews">
                        {interviews.slice(0, 12).map((r) => (
                            <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                                <span className="font-mono text-xs text-foreground/70 w-28 shrink-0">{(r.scheduled_at || "").slice(5, 16).replace("T", " ")}</span>
                                <span className="font-medium truncate">{r.candidate?.name || "Unknown"}</span>
                                <ChevronRight className="w-3 h-3 text-foreground/40 shrink-0" />
                                <span className="truncate text-foreground/80">{r.job?.title || "—"}</span>
                                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-foreground/60 shrink-0">{r.round_name || r.round || "Interview"}</span>
                                {(r.meet_link || r.meeting_url) && (
                                    <a href={r.meet_link || r.meeting_url} target="_blank" rel="noreferrer" className="text-ikb shrink-0" title="Join"><Video className="w-3.5 h-3.5" /></a>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
